import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Assets } from "../../constants/assets";
import ThousandsSeparator from "../../utils/thousands-separator";
import Dialog from "../Dialog";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import "./map-hud.css";

type Props = {
  open: boolean;
  onClose: () => void;
};

/**
 * Wallet / resources modal opened from the top-bar chevron.
 *
 * Row order follows the design mock (Cleg first, then the mined resources,
 * then the arena currencies) — deliberately different from the top-bar pill
 * order, which the mock keeps as-is.
 *
 * Every "+" reuses the top-up destination the legacy Navbar already used for
 * that resource, so there is exactly one buy flow per currency:
 *   marketplace tab types — 1 Stone, 2 Iron, 6 Arena Shop, 7 Ember Shop
 *   (see components/MarketPlace/MarketPlaceTabs).
 */
const RESOURCE_ROWS = [
  {
    key: "cleg",
    label: "common:resources.clegToken",
    icon: "/assets/images/icons/cleg.png",
    field: "totalCleg",
    /** Deposit / withdraw live on the profile page (on-chain CLEG). */
    addTo: "/profile",
    highlight: true,
  },
  {
    key: "stone",
    label: "common:resources.stone",
    icon: "/assets/images/icons/stone.png",
    field: "totalStone",
    addTo: "/marketplace?type=1",
  },
  {
    key: "iron",
    label: "common:resources.iron",
    icon: "/assets/images/icons/metal.png",
    field: "totalIron",
    addTo: "/marketplace?type=2",
  },
  {
    key: "blood",
    label: "common:resources.bloodGem",
    icon: Assets.icons.bloodStone,
    field: "totalBloodGem",
    addTo: "/marketplace?type=6",
  },
  {
    key: "energy",
    label: "common:resources.energy",
    icon: Assets.icons.energy,
    field: "totalEnergy",
    addTo: "/marketplace?type=6",
  },
  {
    key: "ember",
    label: "common:resources.ember",
    icon: Assets.icons.ember1x1,
    field: "totalEmber",
    addTo: "/marketplace?type=7",
  },
];

const ResourcesDialog = ({ open, onClose }: Props) => {
  const { t } = useAppTranslation("common");
  const navigate = useNavigate();
  const userInfo = useSelector((state: any) => state.userInfo);

  const rows = useMemo(
    () =>
      RESOURCE_ROWS.map((r) => ({
        ...r,
        value: Math.floor(Number(userInfo?.[r.field]) || 0),
      })),
    [userInfo]
  );

  const goTo = (to: string) => {
    onClose();
    navigate(to);
  };

  return (
    <Dialog open={open} onClose={onClose} title={t("common:resources.title")} noFooter>
      <div className="res-modal">
        {rows.map((r) => (
          <div
            className={"res-row" + (r.highlight ? " res-row--cleg" : "")}
            key={r.key}
          >
            <img className="res-row-ico" src={r.icon} alt={t(r.label)} />
            <span className="res-row-name">{t(r.label)}</span>
            <span className="res-row-value">{ThousandsSeparator(r.value)}</span>
            <button
              type="button"
              className="res-row-add"
              aria-label={t("common:resources.getMore", { resource: t(r.label) })}
              onClick={() => goTo(r.addTo)}
            />
          </div>
        ))}
      </div>
    </Dialog>
  );
};

export default ResourcesDialog;
